import React, { useMemo, useState } from 'react';

const SORTS = [
  ['p95', 'P95'],
  ['errors', 'Ошибки'],
  ['cost', 'Стоимость'],
  ['mongo', 'Mongo P95'],
  ['response', 'Размер ответа'],
  ['requests', 'Частота'],
];

const formatMs = (value) => {
  const ms = Number(value || 0);
  if (ms >= 1000) return `${(ms / 1000).toFixed(ms >= 10000 ? 1 : 2)} с`;
  return `${Math.round(ms * 10) / 10} мс`;
};

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (!(bytes > 0)) return '0 Б';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const number = bytes / 1024 ** index;
  return `${number >= 100 ? number.toFixed(0) : number.toFixed(1)} ${units[index]}`;
};

const formatTime = (value) => {
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch (_error) {
    return String(value || '—');
  }
};

const toneForLatency = (value) => {
  const ms = Number(value || 0);
  if (ms >= 1000) return 'bad';
  if (ms >= 300) return 'warn';
  return 'good';
};

const redactClipboardText = (value = '') => String(value || '')
  .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [скрыто]')
  .replace(/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, '[JWT скрыт]')
  .replace(/((?:password|passwd|pwd|token|secret|authorization|cookie|api[-_]?key|access[-_]?token|refresh[-_]?token)\s*[:=]\s*)[^\s,;]+/gi, '$1[скрыто]');

const copyToClipboard = async (text) => {
  const safeText = redactClipboardText(text);

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(safeText);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = safeText;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
};

const CopyButton = ({
  text,
  label = 'Скопировать',
  className = '',
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    try {
      await copyToClipboard(typeof text === 'function' ? text() : text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch (_error) {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      className={`systemMonitor__apiCopyBtn ${className}`.trim()}
      onClick={handleCopy}
    >
      {copied ? 'Скопировано' : label}
    </button>
  );
};

const Metric = ({ label, value, tone = 'neutral', hint }) => (
  <div className={`systemMonitor__apiProfilerMetric is-${tone}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {hint ? <small>{hint}</small> : null}
  </div>
);

const formatErrorText = (row = {}) => {
  const response = String(row.errorResponse || '').trim();

  return [
    `[${formatTime(row.ts)}] ${row.method || 'GET'} ${row.requestPath || row.endpoint || '—'}`,
    `Endpoint: ${row.endpoint || '—'}`,
    `HTTP: ${row.status || 0}`,
    `Время: ${formatMs(row.durationMs)}`,
    `Mongo: ${formatMs(row.mongoWallMs)} · ${row.mongoCount || 0} оп.`,
    `Вне Mongo: ${formatMs(row.outsideMongoMsApprox)}`,
    `Ответ: ${formatBytes(row.responseBytes)}`,
    `Источник: ${String(row.source || 'unknown').toUpperCase()}`,
    `Request ID: ${row.requestId || '—'}`,
    `Ответ ошибки: ${response || 'текст ответа отсутствует'}`,
  ].join('\n');
};

const formatTraceText = (row = {}) => [
  `[${formatTime(row.ts)}] ${row.method || 'GET'} ${row.requestPath || row.endpoint || '—'}`,
  `Endpoint: ${row.endpoint || '—'}`,
  `HTTP: ${row.status || 0}`,
  `Время: ${formatMs(row.durationMs)}`,
  `Mongo реальное: ${formatMs(row.mongoWallMs ?? row.mongoTotalMs)}`,
  `Mongo сумма: ${formatMs(row.mongoTotalMs)}`,
  `Mongo операций: ${row.mongoCount || 0}`,
  `Самая долгая Mongo: ${formatMs(row.mongoLongestMs)}`,
  `Вне Mongo: ${formatMs(row.outsideMongoMsApprox)}`,
  `Ответ: ${formatBytes(row.responseBytes)}`,
  row.errorResponse ? `Ответ ошибки: ${row.errorResponse}` : '',
].filter(Boolean).join('\n');

const formatEndpointText = (row = {}) => {
  const mongoLines = (row.topMongoOperations || []).map((operation) => (
    `  - ${operation.collection}.${operation.operation}: ${operation.calls} выз., ` +
    `среднее ${formatMs(operation.avgMs)}, MAX ${formatMs(operation.maxMs)}, всего ${formatMs(operation.totalMs)}`
  ));

  const errorLines = (row.recentErrors || []).map((error, index) => (
    `\nОшибка ${index + 1}:\n${formatErrorText(error)}`
  ));

  return [
    `${row.method || 'GET'} ${row.endpoint || '—'}`,
    `Запросов: ${row.requests || 0}`,
    `5xx: ${row.server5xx || 0} · 4xx: ${row.client4xx || 0} · всего ошибок: ${row.errors || 0}`,
    `Среднее: ${formatMs(row.avgMs)} · P50: ${formatMs(row.p50Ms)} · P95: ${formatMs(row.p95Ms)} · MAX: ${formatMs(row.maxMs)}`,
    `Mongo P95: ${formatMs(row.p95MongoMs)} · Mongo/запрос: ${row.avgMongoOps || 0}`,
    `Вне Mongo, оценка: ${formatMs(row.avgOutsideMongoMsApprox)}`,
    `Ответ P95: ${formatBytes(row.p95ResponseBytes)} · MAX: ${formatBytes(row.maxResponseBytes)}`,
    `Стоимость: ${formatMs(row.totalDurationMs)} · доля окна: ${row.costPercent || 0}%`,
    mongoLines.length ? `Mongo-операции:\n${mongoLines.join('\n')}` : 'Mongo-операции: нет подробной трассы',
    errorLines.length ? `Последние ошибки:${errorLines.join('\n')}` : 'Последние ошибки: нет',
  ].join('\n');
};

const getSignals = (row) => {
  const signals = [];
  const p95 = Number(row.p95Ms || 0);
  const mongoP95 = Number(row.p95MongoMs || 0);
  const mongoOps = Number(row.avgMongoOps || 0);
  const response = Number(row.p95ResponseBytes || 0);
  const requests = Number(row.requests || 0);
  const server5xx = Number(row.server5xx || 0);
  const client4xx = Number(row.client4xx || 0);

  if (server5xx > 0) signals.push(['bad', `5xx: ${server5xx}`]);
  else if (client4xx > 0) signals.push(['warn', `4xx: ${client4xx}`]);

  if (p95 >= 1000) signals.push(['bad', 'Критично медленно']);
  else if (p95 >= 500) signals.push(['warn', 'Медленно']);
  else if (p95 >= 300) signals.push(['warn', 'Проверить']);

  if (mongoP95 >= 25 && p95 > 0 && mongoP95 / p95 >= 0.55) {
    signals.push(['mongo', 'Основное время — MongoDB']);
  } else if (p95 >= 200 && mongoP95 < p95 * 0.3) {
    signals.push(['node', 'Искать в Node/логике']);
  }

  if (mongoOps >= 8) signals.push(['warn', `Много Mongo: ${Math.round(mongoOps)}/запрос`]);
  if (response >= 1024 * 1024) signals.push(['warn', 'Ответ > 1 МБ']);
  else if (response >= 512 * 1024) signals.push(['neutral', 'Ответ > 512 КБ']);
  if (requests >= 30) signals.push(['neutral', 'Часто вызывается']);

  if (!signals.length) signals.push(['good', 'Выглядит нормально']);
  return signals.slice(0, 4);
};

const ErrorRow = ({ row, expanded, onToggle }) => (
  <article className={`systemMonitor__apiErrorRow ${Number(row.status || 0) >= 500 ? 'is-5xx' : 'is-4xx'}`}>
    <button type="button" className="systemMonitor__apiErrorMain" onClick={onToggle}>
      <span>{formatTime(row.ts)}</span>
      <b className={`is-status is-${Number(row.status || 0) >= 500 ? '5xx' : '4xx'}`}>
        HTTP {row.status || 0}
      </b>
      <strong>{row.method}</strong>
      <code>{row.endpoint}</code>
      <span>{formatMs(row.durationMs)}</span>
      <span>Mongo {formatMs(row.mongoWallMs)}</span>
      <i>{expanded ? '−' : '+'}</i>
    </button>

    <CopyButton text={() => formatErrorText(row)} className="is-inline" />

    {expanded ? (
      <div className="systemMonitor__apiErrorDetails">
        <div className="systemMonitor__apiTraceSummary">
          <span>Путь <b>{row.requestPath || row.endpoint}</b></span>
          <span>Request ID <b>{row.requestId || '—'}</b></span>
          <span>Mongo операций <b>{row.mongoCount || 0}</b></span>
          <span>Вне Mongo <b>{formatMs(row.outsideMongoMsApprox)}</b></span>
          <span>Ответ <b>{formatBytes(row.responseBytes)}</b></span>
        </div>
        <div className="systemMonitor__apiErrorResponseTitle">Ответ BACK</div>
        <pre className="systemMonitor__apiErrorResponse">
          {String(row.errorResponse || '').trim() || 'Текст ответа отсутствует.'}
        </pre>
      </div>
    ) : null}
  </article>
);

const EndpointProfile = ({ row, expanded, onToggle }) => {
  const signals = getSignals(row);
  const mongoOperations = row.topMongoOperations || [];
  const recentErrors = row.recentErrors || [];

  return (
    <article className={`systemMonitor__apiEndpoint is-${toneForLatency(row.p95Ms)}`}>
      <button type="button" className="systemMonitor__apiEndpointMain" onClick={onToggle}>
        <div className="systemMonitor__apiEndpointIdentity">
          <b className={`is-method is-${String(row.method || '').toLowerCase()}`}>{row.method}</b>
          <code>{row.endpoint}</code>
        </div>

        <div className="systemMonitor__apiEndpointSignals">
          {signals.map(([tone, label]) => (
            <span key={`${tone}:${label}`} className={`is-${tone}`}>{label}</span>
          ))}
        </div>

        <div className="systemMonitor__apiEndpointMetrics">
          <span><small>Запросов</small><b>{row.requests || 0}</b></span>
          <span><small>Среднее</small><b>{formatMs(row.avgMs)}</b></span>
          <span><small>P50</small><b>{formatMs(row.p50Ms)}</b></span>
          <span><small>P95</small><b>{formatMs(row.p95Ms)}</b></span>
          <span><small>MAX</small><b>{formatMs(row.maxMs)}</b></span>
          <span><small>Mongo P95</small><b>{formatMs(row.p95MongoMs)}</b></span>
          <span><small>Mongo/запрос</small><b>{row.avgMongoOps || 0}</b></span>
          <span><small>Ответ P95</small><b>{formatBytes(row.p95ResponseBytes)}</b></span>
          <span><small>Стоимость</small><b>{formatMs(row.totalDurationMs)}</b></span>
        </div>

        <div className="systemMonitor__apiEndpointChevron">{expanded ? '−' : '+'}</div>
      </button>

      <CopyButton text={() => formatEndpointText(row)} className="is-inline" />

      {expanded ? (
        <div className="systemMonitor__apiEndpointDetails">
          <div className="systemMonitor__apiEndpointDetailGrid">
            <div><span>Доля времени окна</span><b>{row.costPercent || 0}%</b></div>
            <div><span>Mongo реальное среднее</span><b>{formatMs(row.avgMongoMs)}</b></div>
            <div><span>Mongo реальное MAX</span><b>{formatMs(row.maxMongoMs)}</b></div>
            <div><span>Mongo сумма операций, среднее</span><b>{formatMs(row.avgMongoWorkMs ?? row.avgMongoMs)}</b></div>
            <div><span>Самый долгий Mongo P95</span><b>{formatMs(row.p95MongoLongestMs)}</b></div>
            <div><span>Вне Mongo, оценка</span><b>{formatMs(row.avgOutsideMongoMsApprox)}</b></div>
            <div><span>Ответ средний</span><b>{formatBytes(row.avgResponseBytes)}</b></div>
            <div><span>Ответ MAX</span><b>{formatBytes(row.maxResponseBytes)}</b></div>
            <div><span>Ошибок всего</span><b>{row.errors || 0}</b></div>
            <div><span>5xx</span><b className={row.server5xx ? 'is-danger' : ''}>{row.server5xx || 0}</b></div>
            <div><span>4xx</span><b>{row.client4xx || 0}</b></div>
          </div>

          {recentErrors.length ? (
            <>
              <div className="systemMonitor__apiMongoTitle">Последние ошибки этого endpoint</div>
              <div className="systemMonitor__apiEndpointErrors">
                {recentErrors.map((error, index) => (
                  <div key={error.requestId || `${error.ts}:${index}`}>
                    <span>{formatTime(error.ts)}</span>
                    <b>HTTP {error.status}</b>
                    <code>{String(error.errorResponse || '').trim() || 'без текста ответа'}</code>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          <div className="systemMonitor__apiMongoTitle">Самые дорогие Mongo-операции внутри endpoint</div>
          {mongoOperations.length ? (
            <div className="systemMonitor__apiMongoList">
              {mongoOperations.map((operation, index) => (
                <div key={`${operation.collection}:${operation.operation}:${index}`}>
                  <code>{operation.collection}.{operation.operation}</code>
                  <span>{operation.calls} выз.</span>
                  <span>среднее {formatMs(operation.avgMs)}</span>
                  <span>MAX {formatMs(operation.maxMs)}</span>
                  <b>всего {formatMs(operation.totalMs)}</b>
                </div>
              ))}
            </div>
          ) : (
            <div className="systemMonitor__apiProfilerEmpty">
              Подробная Mongo-трассировка сохраняется для запросов от 50 мс, ошибок и крупных ответов.
            </div>
          )}
        </div>
      ) : null}
    </article>
  );
};

const RequestTrace = ({ row, expanded, onToggle }) => (
  <article className="systemMonitor__apiTrace">
    <button type="button" onClick={onToggle}>
      <span>{new Date(row.ts).toLocaleTimeString('ru-RU')}</span>
      <b>{row.method}</b>
      <code>{row.endpoint}</code>
      <strong className={`is-${toneForLatency(row.durationMs)}`}>{formatMs(row.durationMs)}</strong>
      <small>Mongo {formatMs(row.mongoWallMs ?? row.mongoTotalMs)} · {row.mongoCount || 0} оп.</small>
      <small>{formatBytes(row.responseBytes)}</small>
    </button>

    <CopyButton text={() => formatTraceText(row)} className="is-inline" />

    {expanded ? (
      <div className="systemMonitor__apiTraceDetails">
        <div className="systemMonitor__apiTraceSummary">
          <span>HTTP <b>{row.status}</b></span>
          <span>Mongo реальное время <b>{formatMs(row.mongoWallMs ?? row.mongoTotalMs)}</b></span>
          <span>Mongo сумма операций <b>{formatMs(row.mongoTotalMs)}</b></span>
          <span>Самая долгая Mongo <b>{formatMs(row.mongoLongestMs)}</b></span>
          <span>Вне Mongo, оценка <b>{formatMs(row.outsideMongoMsApprox)}</b></span>
          <span>Ответ <b>{formatBytes(row.responseBytes)}</b></span>
        </div>

        {row.errorResponse ? (
          <>
            <div className="systemMonitor__apiErrorResponseTitle">Ответ ошибки</div>
            <pre className="systemMonitor__apiErrorResponse">{row.errorResponse}</pre>
          </>
        ) : null}

        {(row.mongoOperations || []).length ? (
          <div className="systemMonitor__apiMongoList">
            {(row.mongoOperations || []).map((operation, index) => (
              <div key={`${operation.collection}:${operation.operation}:${index}`}>
                <code>{operation.collection}.{operation.operation}</code>
                <span>{formatMs(operation.durationMs)}</span>
                <span>{operation.resultCount !== null ? `${operation.resultCount} рез.` : '—'}</span>
                <b>{operation.error ? 'ошибка' : 'OK'}</b>
              </div>
            ))}
          </div>
        ) : (
          <div className="systemMonitor__apiProfilerEmpty">Для этого запроса подробных Mongo-операций нет.</div>
        )}
      </div>
    ) : null}
  </article>
);

const SystemMonitorApiProfiler = ({
  api = {},
  source = 'all',
  onReset,
  resetting = false,
}) => {
  const [sortMode, setSortMode] = useState('p95');
  const [expandedEndpoint, setExpandedEndpoint] = useState('');
  const [expandedTrace, setExpandedTrace] = useState('');
  const [expandedError, setExpandedError] = useState('');

  const endpointRows = useMemo(() => {
    const rows = [...(api.endpointProfiles || api.topEndpoints || [])];

    const sorters = {
      p95: (a, b) => Number(b.p95Ms || 0) - Number(a.p95Ms || 0),
      errors: (a, b) => (
        (Number(b.server5xx || 0) * 100000 + Number(b.client4xx || 0) * 1000 + Number(b.errors || 0))
        - (Number(a.server5xx || 0) * 100000 + Number(a.client4xx || 0) * 1000 + Number(a.errors || 0))
      ) || Number(b.p95Ms || 0) - Number(a.p95Ms || 0),
      cost: (a, b) => Number(b.totalDurationMs || 0) - Number(a.totalDurationMs || 0),
      mongo: (a, b) => Number(b.p95MongoMs || 0) - Number(a.p95MongoMs || 0),
      response: (a, b) => Number(b.p95ResponseBytes || 0) - Number(a.p95ResponseBytes || 0),
      requests: (a, b) => Number(b.requests || 0) - Number(a.requests || 0),
    };

    return rows.sort(sorters[sortMode] || sorters.p95).slice(0, 60);
  }, [api.endpointProfiles, api.topEndpoints, sortMode]);

  const traces = useMemo(
    () => [...(api.slowRequests || [])]
      .sort((a, b) => Number(b.durationMs || 0) - Number(a.durationMs || 0))
      .slice(0, 30),
    [api.slowRequests],
  );

  const errors = useMemo(
    () => [...(api.errorRequests || [])]
      .sort((a, b) => {
        const severityDelta = Number(b.status >= 500) - Number(a.status >= 500);
        if (severityDelta) return severityDelta;
        return Number(b.ts || 0) - Number(a.ts || 0);
      })
      .slice(0, 60),
    [api.errorRequests],
  );

  const sourceLabel = source === 'front' ? 'САЙТ' : source === 'admin' ? 'АДМИНКА' : 'всего проекта';

  const allDiagnosticsText = useMemo(() => {
    const summary = [
      `SYSTEM MONITOR · API · ${sourceLabel}`,
      `Сформировано: ${new Date().toLocaleString('ru-RU')}`,
      `Запросов: ${api.requests || 0} · ${api.requestsPerMinute || 0}/мин`,
      `Среднее: ${formatMs(api.avgMs)} · P50: ${formatMs(api.p50Ms)} · P95: ${formatMs(api.p95Ms)} · MAX: ${formatMs(api.maxMs)}`,
      `Mongo P95: ${formatMs(api.p95MongoMs)} · среднее операций: ${api.avgMongoOps || 0}`,
      `Ответ P95: ${formatBytes(api.p95ResponseBytes)} · MAX: ${formatBytes(api.maxResponseBytes)}`,
      `5xx: ${api.status?.server5xx || 0} · 4xx: ${api.status?.client4xx || 0}`,
    ];

    const errorText = errors.length
      ? errors.map((row, index) => `\n--- ОШИБКА ${index + 1} ---\n${formatErrorText(row)}`).join('\n')
      : '\nОшибок API в текущем окне нет.';

    const endpointText = endpointRows.length
      ? endpointRows.map((row, index) => `\n--- ENDPOINT ${index + 1} ---\n${formatEndpointText(row)}`).join('\n')
      : '\nEndpoint-статистика отсутствует.';

    const traceText = traces.length
      ? traces.map((row, index) => `\n--- ЗАПРОС ${index + 1} ---\n${formatTraceText(row)}`).join('\n')
      : '\nМедленных запросов нет.';

    return `${summary.join('\n')}\n\n=== ОШИБКИ ===${errorText}\n\n=== ENDPOINT-ПРОФИЛЬ ===${endpointText}\n\n=== ДОЛГИЕ ЗАПРОСЫ ===${traceText}`;
  }, [api, endpointRows, errors, sourceLabel, traces]);

  return (
    <div className="systemMonitor__apiProfiler">
      <section className="systemMonitor__apiProfilerHead">
        <div>
          <div className="systemMonitor__eyebrow">ПРОФИЛИРОВЩИК API · {sourceLabel}</div>
          <h2>Где сайт тратит время</h2>
          <p>
            P95 показывает хвост задержки, «Стоимость» — суммарное время endpoint.
            Mongo P95 показывает реальное время MongoDB. Ошибки 4xx/5xx теперь
            сохраняются с HTTP-кодом и безопасно очищенным текстом ответа BACK.
          </p>
        </div>

        <div className="systemMonitor__apiProfilerHeadActions">
          <CopyButton text={allDiagnosticsText} label="Скопировать всю диагностику" />
          <button
            type="button"
            className="systemMonitor__apiResetBtn"
            disabled={resetting}
            onClick={onReset}
          >
            {resetting ? 'Очищаю…' : 'Очистить API-статистику'}
          </button>
        </div>
      </section>

      <div className="systemMonitor__apiProfilerKpis">
        <Metric label="Запросов" value={api.requests || 0} hint={`${api.requestsPerMinute || 0}/мин`} />
        <Metric label="Среднее" value={formatMs(api.avgMs)} tone={toneForLatency(api.avgMs)} />
        <Metric label="P50" value={formatMs(api.p50Ms)} tone={toneForLatency(api.p50Ms)} />
        <Metric label="P95" value={formatMs(api.p95Ms)} tone={toneForLatency(api.p95Ms)} />
        <Metric label="MAX" value={formatMs(api.maxMs)} tone={toneForLatency(api.maxMs)} />
        <Metric label="Mongo P95" value={formatMs(api.p95MongoMs)} tone={toneForLatency(api.p95MongoMs)} hint={`${api.avgMongoOps || 0} оп./запрос`} />
        <Metric label="Ответ P95" value={formatBytes(api.p95ResponseBytes)} hint={`MAX ${formatBytes(api.maxResponseBytes)}`} />
        <Metric label="5xx / 4xx" value={`${api.status?.server5xx || 0} / ${api.status?.client4xx || 0}`} tone={(api.status?.server5xx || 0) ? 'bad' : (api.status?.client4xx || 0) ? 'warn' : 'good'} />
      </div>

      {(errors.length || Number(api.status?.server5xx || 0) || Number(api.status?.client4xx || 0)) ? (
        <section className="systemMonitor__apiProfilerSection is-errors">
          <div className="systemMonitor__apiProfilerSectionHead">
            <div>
              <div className="systemMonitor__sectionTitle">Ошибки API</div>
              <div className="systemMonitor__sectionHint">
                Нажми на ошибку — увидишь конкретный HTTP-код, путь, время и текст ответа BACK.
              </div>
            </div>
            <CopyButton
              text={() => errors.map((row, index) => `--- ОШИБКА ${index + 1} ---\n${formatErrorText(row)}`).join('\n\n')}
              label="Скопировать все ошибки"
            />
          </div>

          <div className="systemMonitor__apiErrorList">
            {errors.map((row, index) => {
              const key = row.requestId || `${row.ts}:${row.method}:${row.endpoint}:${index}`;
              return (
                <ErrorRow
                  key={key}
                  row={row}
                  expanded={expandedError === key}
                  onToggle={() => setExpandedError((current) => current === key ? '' : key)}
                />
              );
            })}
            {!errors.length ? (
              <div className="systemMonitor__apiProfilerEmpty">
                Счётчик ошибок есть, но подробная запись для этого окна не сохранилась. Очисти статистику и воспроизведи запрос ещё раз.
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="systemMonitor__apiProfilerSection">
        <div className="systemMonitor__apiProfilerSectionHead">
          <div>
            <div className="systemMonitor__sectionTitle">Endpoint-профиль</div>
            <div className="systemMonitor__sectionHint">
              Нажми на endpoint — увидишь Mongo-операции, последние ошибки и станет понятно, база это, Node.js или большой ответ.
            </div>
          </div>

          <div className="systemMonitor__apiSectionActions">
            <CopyButton
              text={() => endpointRows.map((row, index) => `--- ENDPOINT ${index + 1} ---\n${formatEndpointText(row)}`).join('\n\n')}
              label="Скопировать весь блок"
            />
            <div className="systemMonitor__apiSort">
              {SORTS.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={sortMode === key ? 'is-active' : ''}
                  onClick={() => setSortMode(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="systemMonitor__apiEndpointList">
          {endpointRows.map((row) => {
            const key = `${row.method}:${row.endpoint}`;
            return (
              <EndpointProfile
                key={key}
                row={row}
                expanded={expandedEndpoint === key}
                onToggle={() => setExpandedEndpoint((current) => current === key ? '' : key)}
              />
            );
          })}
          {!endpointRows.length ? <div className="systemMonitor__apiProfilerEmpty">Пока запросов нет.</div> : null}
        </div>
      </section>

      <section className="systemMonitor__apiProfilerSection">
        <div className="systemMonitor__apiProfilerSectionHead">
          <div>
            <div className="systemMonitor__sectionTitle">Самые долгие отдельные запросы</div>
            <div className="systemMonitor__sectionHint">
              Это конкретные вызовы из текущего окна. Нажми на строку, чтобы увидеть Mongo-трассу и ответ ошибки, если он был.
            </div>
          </div>
          <div className="systemMonitor__apiSectionActions">
            <CopyButton
              text={() => traces.map((row, index) => `--- ЗАПРОС ${index + 1} ---\n${formatTraceText(row)}`).join('\n\n')}
              label="Скопировать весь блок"
            />
            <span className="systemMonitor__apiProfilerBadge">TOP {traces.length}</span>
          </div>
        </div>

        <div className="systemMonitor__apiTraceList">
          {traces.map((row, index) => {
            const key = row.requestId || `${row.ts}:${row.method}:${row.endpoint}:${index}`;
            return (
              <RequestTrace
                key={key}
                row={row}
                expanded={expandedTrace === key}
                onToggle={() => setExpandedTrace((current) => current === key ? '' : key)}
              />
            );
          })}
          {!traces.length ? <div className="systemMonitor__apiProfilerEmpty">Пока запросов нет.</div> : null}
        </div>
      </section>

      <section className="systemMonitor__apiReadGuide">
        <b>Как быстро читать:</b>
        <span><i className="is-mongo" /> Mongo P95 близок к API P95 → основное реальное время уходит в MongoDB.</span>
        <span><i className="is-node" /> API P95 высокий, Mongo низкий → ищем Node.js, циклы, файлы, сериализацию.</span>
        <span><i className="is-response" /> Ответ сотни КБ/МБ → уменьшаем projection/payload или кэшируем.</span>
        <span><i className="is-cost" /> 5xx/4xx → раскрываем «Ошибки API» и копируем точный ответ BACK.</span>
      </section>
    </div>
  );
};

export default SystemMonitorApiProfiler;
